import React, { useEffect, useState } from "react";
import api, { fileUrl } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Printer, X, Phone, Mail } from "lucide-react";

const fmt = (iso) => (iso ? iso.slice(0, 16).replace("T", " ") : "—");

/**
 * Small deposit receipt (80mm thermal-printer style) handed to the customer:
 * card number, time stored, items, served by and lost&found contacts.
 */
export default function LuggageReceipt({ locker, session, onClose }) {
  const [tenant, setTenant] = useState(null);

  useEffect(() => {
    api.get("/tenants/me").then(({ data }) => setTenant(data)).catch(() => {});
    document.body.classList.add("printing-cards");
    return () => document.body.classList.remove("printing-cards");
  }, []);

  const brandName = tenant?.name || "Abrah Supermarket Ltd";

  return (
    <div className="fixed inset-0 z-[80] bg-gray-100 overflow-y-auto" data-testid="lug-receipt-sheet">
      <div className="print-hide sticky top-0 z-10 bg-white border-b border-gray-200 px-4 py-3 flex items-center gap-3">
        <div className="text-sm font-semibold text-gray-800">Deposit receipt — {locker.locker_no}</div>
        <div className="ml-auto flex gap-2">
          <Button size="sm" onClick={() => window.print()}
            className="bg-emerald-900 hover:bg-emerald-800" data-testid="lug-receipt-print">
            <Printer size={14} className="mr-1.5" /> Print receipt
          </Button>
          <Button size="sm" variant="outline" onClick={onClose} data-testid="lug-receipt-close">
            <X size={14} className="mr-1.5" /> Close
          </Button>
        </div>
      </div>

      <div className="print-area py-8 flex justify-center bg-white min-h-[calc(100vh-57px)]">
        <div className="lug-card border border-dashed border-gray-400 bg-white p-4 h-fit"
          style={{ width: "80mm" }} data-testid="lug-receipt-body">
          {/* Brand */}
          <div className="flex items-center gap-2 pb-2 border-b border-gray-300">
            <div className="h-8 w-8 rounded bg-[#022C22] flex items-center justify-center overflow-hidden shrink-0">
              {tenant?.logo_file_id ? (
                <img src={fileUrl(tenant.logo_file_id)} alt="logo" className="h-full w-full object-contain bg-white" />
              ) : /abrah/i.test(brandName) ? (
                <img src="/abrah-logo.jpeg" alt="Abrah Supermarket logo" className="h-full w-full object-contain bg-white" data-testid="lug-receipt-default-logo" />
              ) : (
                <span className="text-amber-400 font-bold">{brandName.slice(0, 1)}</span>
              )}
            </div>
            <div className="leading-tight">
              <div className="text-[11px] font-bold text-gray-900">{brandName}</div>
              <div className="text-[8px] uppercase tracking-[0.2em] text-gray-500">Luggage deposit receipt</div>
            </div>
          </div>

          {/* Card number */}
          <div className="text-center py-3 border-b border-gray-300">
            <div className="text-[8px] uppercase tracking-[0.18em] text-gray-500">Card number</div>
            <div className="font-display text-4xl font-extrabold text-emerald-900 leading-none mt-1">
              {locker.locker_no}
            </div>
            <div className="text-[9px] text-gray-500 mt-1">{locker.branch}</div>
          </div>

          {/* Details */}
          <div className="py-2.5 space-y-1.5 text-[10px] text-gray-700 border-b border-gray-300">
            <div className="flex justify-between"><span className="text-gray-500">Stored at</span><span className="font-semibold">{fmt(session?.checked_in_at)}</span></div>
            {session?.customer_name && (
              <div className="flex justify-between"><span className="text-gray-500">Customer</span><span className="font-semibold">{session.customer_name}</span></div>
            )}
            {session?.customer_phone && (
              <div className="flex justify-between"><span className="text-gray-500">Phone</span><span>{session.customer_phone}</span></div>
            )}
            {session?.items_description && (
              <div><span className="text-gray-500">Items: </span><span>{session.items_description}</span></div>
            )}
            {session?.checked_in_by_name && (
              <div className="flex justify-between"><span className="text-gray-500">Served by</span><span>{session.checked_in_by_name}</span></div>
            )}
          </div>

          {/* Footer */}
          <div className="pt-2.5 text-[9px] text-gray-600 space-y-1">
            <div className="font-semibold text-gray-800">Present your entry card (or this receipt) to collect your luggage.</div>
            <div className="text-gray-500">Lost or found a card?</div>
            {locker.contact_phone && (
              <div className="flex items-center gap-1"><Phone size={9} /> {locker.contact_phone}</div>
            )}
            {locker.contact_email && (
              <div className="flex items-center gap-1"><Mail size={9} /> {locker.contact_email}</div>
            )}
            <div className="text-center text-gray-400 pt-1.5">Thank you for shopping with us.</div>
          </div>
        </div>
      </div>
    </div>
  );
}
